/**
 * `POST /api/v1/report-schedules` — the create half of the schedule lifecycle.
 *
 * **C, R, U and D all exist now; only the collection read does not.** This file used
 * to say the opposite, and it was true when it was written: `routes.ts` mounted one
 * route. It now mounts four — `POST` on the collection, `GET`/`PATCH`/`DELETE` on
 * `/{id}` — so the read, the pause and the removal live in `ScheduleManager`, and
 * `AbsentEndpoints` names only what is genuinely still missing: `GET
 * /report-schedules`, the list, which is why the manager works on one id.
 *
 * ── The day field is absent for a frequency that has no day ────────────────
 *
 * `wp_sched_weekly_ck` requires `day_of_week` for `weekly` and **refuses** it
 * otherwise; `wp_sched_monthly_ck` does the same for `day_of_month` and `monthly`.
 * So the field is not rendered at all for a frequency it does not belong to. That is
 * absence for the honest reason rather than the house rule: on a daily schedule
 * `day_of_week` is not a field this operator is unqualified to fill in, it is a field
 * that does not exist, and a greyed input would invite them to go and ask for the
 * permission to use it.
 *
 * ── `day_of_month` stops at 28, and the input says so ──────────────────────
 *
 * 29 to 31 are refused, never clamped. Clamping is the bug the constraint exists to
 * prevent: a "monthly on the 31st" payroll export would fire on the 31st, 30th, 29th
 * or 28th depending on the month with the row still saying 31, and the drift would be
 * invisible to everybody downstream.
 *
 * ── The id is handed on rather than merely printed ─────────────────────────
 *
 * The `201` body used to be the only sight of the id, so the note told the operator
 * to write it down. `GET /report-schedules/{id}` exists now, so the id goes to
 * `ScheduleManager` through `onCreated` and the record is open below before the note
 * is read. The note still prints the id, because **there is no list**: after a reload
 * the id remains the only route back to the record, and that is a different sentence
 * from the one it used to make.
 */

import { useEffect, useState, type ReactNode } from 'react';

import { useI18n } from '../../i18n';
import type { MessageKey } from '../../i18n/messages';
import { REPORT_SCHEDULES_PATH } from '../../api/paths';
import type {
  ReportFormatName,
  ReportSchedule,
  ReportScheduleCreateRequest,
  ReportTypeName,
  ScheduleFrequency,
  SchedulePeriod,
} from '../../api/types';
import { useApiMutation } from '../../api/useApiMutation';
import { REPORT_FORMATS, REPORT_TYPES, hasBuilder } from '../../reports/catalog';
import {
  SCHEDULE_DAY_OF_MONTH_MAX,
  SCHEDULE_FREQUENCIES,
  SCHEDULE_PERIODS,
  dayFieldFor,
  scheduleProblems,
  scheduleRequestBody,
  type ScheduleDraft,
} from '../../reports/schedule';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { StatusNote } from '../../states/StatusNote';

const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7] as const;

export interface ScheduleFormProps {
  /**
   * Raised once per successful create, with the `201` body.
   *
   * Optional, so the form still stands alone in a test that only cares about the
   * request body — but the screen always passes one, because a created schedule that
   * nothing opened would be back to "write the id down or lose it".
   */
  readonly onCreated?: (schedule: ReportSchedule) => void;
}

export function ScheduleForm({ onCreated }: ScheduleFormProps = {}): ReactNode {
  const { t } = useI18n();
  const [draft, setDraft] = useState<ScheduleDraft>({
    name: '',
    reportType: 'attendance',
    formats: ['csv'],
    frequency: 'daily',
    dayOfWeek: '1',
    dayOfMonth: '1',
    hourOfDay: '7',
    minuteOfHour: '0',
    timezone: '',
    period: 'previous_day',
    recipients: '',
    enabled: true,
  });
  const create = useApiMutation<ReportScheduleCreateRequest, { data: ReportSchedule }>(
    REPORT_SCHEDULES_PATH,
  );

  const problems = scheduleProblems(draft);
  const dayField = dayFieldFor(draft.frequency);
  const pending = create.status === 'pending';
  const created = create.status === 'ok' ? (create.data?.data ?? null) : null;

  // The handoff is derived from the mutation's own result rather than fired inside
  // `submit(...).then()`, where `create.data` would still be the previous render's
  // value. Hand-checked dependency list — there is no `react-hooks` plugin in this
  // repository — and `created` only changes identity when a `201` body arrives, so a
  // re-render for an unrelated keystroke does not raise it a second time.
  useEffect(() => {
    if (created !== null) onCreated?.(created);
  }, [created, onCreated]);

  function change(patch: Partial<ScheduleDraft>): void {
    setDraft((current) => ({ ...current, ...patch }));
  }

  function toggleFormat(format: ReportFormatName): void {
    setDraft((current) => ({
      ...current,
      formats: current.formats.includes(format)
        ? current.formats.filter((entry) => entry !== format)
        : [...current.formats, format],
    }));
  }

  return (
    <section className="wp-section" aria-labelledby="k8-sched-heading" data-testid="k8-schedule">
      <h2 className="wp-section__title" id="k8-sched-heading">
        {t('k8.sched.title')}
      </h2>
      <p className="wp-small wp-muted" data-testid="k8-sched-intro">
        {t('k8.sched.intro')}
      </p>

      <form
        className="wp-filters wp-filters--bar"
        aria-labelledby="k8-sched-heading"
        onSubmit={(event) => {
          event.preventDefault();
          if (problems.length > 0 || pending) return;
          void create.submit(scheduleRequestBody(draft));
        }}
      >
        <div className="wp-field">
          <label className="wp-field__label" htmlFor="k8-sched-name">
            {t('k8.sched.name')}
          </label>
          <input
            className="wp-field__input"
            id="k8-sched-name"
            data-testid="k8-sched-name"
            type="text"
            autoComplete="off"
            value={draft.name}
            onChange={(event) => {
              change({ name: event.target.value });
            }}
          />
        </div>

        <div className="wp-field">
          <label className="wp-field__label" htmlFor="k8-sched-type">
            {t('k8.sched.type')}
          </label>
          <select
            className="wp-field__input"
            id="k8-sched-type"
            data-testid="k8-sched-type"
            value={draft.reportType}
            onChange={(event) => {
              change({ reportType: event.target.value as ReportTypeName });
            }}
          >
            {REPORT_TYPES.map((type) => (
              <option key={type} value={type} data-builder={hasBuilder(type) ? 'yes' : 'no'}>
                {t(`k8.reports.type.${type}` as MessageKey)}
              </option>
            ))}
          </select>
        </div>

        <fieldset className="wp-filters__group">
          <legend className="wp-filters__legend">{t('k8.sched.formats')}</legend>
          {REPORT_FORMATS.map((format) => (
            <label className="wp-filters__radio" key={format} htmlFor={`k8-sched-fmt-${format}`}>
              <input
                id={`k8-sched-fmt-${format}`}
                data-testid={`k8-sched-fmt-${format}`}
                type="checkbox"
                checked={draft.formats.includes(format)}
                onChange={() => {
                  toggleFormat(format);
                }}
              />
              {format}
            </label>
          ))}
        </fieldset>

        <div className="wp-field">
          <label className="wp-field__label" htmlFor="k8-sched-frequency">
            {t('k8.sched.frequency')}
          </label>
          <select
            className="wp-field__input"
            id="k8-sched-frequency"
            data-testid="k8-sched-frequency"
            value={draft.frequency}
            onChange={(event) => {
              change({ frequency: event.target.value as ScheduleFrequency });
            }}
          >
            {SCHEDULE_FREQUENCIES.map((frequency) => (
              <option key={frequency} value={frequency}>
                {t(`k8.sched.freq.${frequency}` as MessageKey)}
              </option>
            ))}
          </select>
        </div>

        {/* Absent, not disabled: `wp_sched_weekly_ck` and `wp_sched_monthly_ck` refuse
            the field that does not belong to this frequency, so for a daily schedule
            it is not a control the operator may not use — it is not a control. */}
        {dayField === 'day_of_week' ? (
          <div className="wp-field">
            <label className="wp-field__label" htmlFor="k8-sched-dow">
              {t('k8.sched.dayOfWeek')}
            </label>
            <select
              className="wp-field__input"
              id="k8-sched-dow"
              data-testid="k8-sched-dow"
              value={draft.dayOfWeek}
              onChange={(event) => {
                change({ dayOfWeek: event.target.value });
              }}
            >
              {WEEKDAYS.map((day) => (
                <option key={day} value={String(day)}>
                  {t(`k8.sched.dow.${String(day)}` as MessageKey)}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        {dayField === 'day_of_month' ? (
          <div className="wp-field">
            <label className="wp-field__label" htmlFor="k8-sched-dom">
              {t('k8.sched.dayOfMonth')}
            </label>
            <input
              className="wp-field__input"
              id="k8-sched-dom"
              data-testid="k8-sched-dom"
              type="number"
              min={1}
              max={SCHEDULE_DAY_OF_MONTH_MAX}
              value={draft.dayOfMonth}
              onChange={(event) => {
                change({ dayOfMonth: event.target.value });
              }}
            />
            <p className="wp-small wp-muted" data-testid="k8-sched-dom-hint">
              {t('k8.sched.dayOfMonthHint')}
            </p>
          </div>
        ) : null}

        <div className="wp-field">
          <label className="wp-field__label" htmlFor="k8-sched-hour">
            {t('k8.sched.hour')}
          </label>
          <input
            className="wp-field__input"
            id="k8-sched-hour"
            data-testid="k8-sched-hour"
            type="number"
            min={0}
            max={23}
            value={draft.hourOfDay}
            onChange={(event) => {
              change({ hourOfDay: event.target.value });
            }}
          />
        </div>

        <div className="wp-field">
          <label className="wp-field__label" htmlFor="k8-sched-minute">
            {t('k8.sched.minute')}
          </label>
          <input
            className="wp-field__input"
            id="k8-sched-minute"
            data-testid="k8-sched-minute"
            type="number"
            min={0}
            max={59}
            value={draft.minuteOfHour}
            onChange={(event) => {
              change({ minuteOfHour: event.target.value });
            }}
          />
        </div>

        <div className="wp-field">
          <label className="wp-field__label" htmlFor="k8-sched-tz">
            {t('k8.sched.timezone')}
          </label>
          <input
            className="wp-field__input"
            id="k8-sched-tz"
            data-testid="k8-sched-tz"
            type="text"
            autoComplete="off"
            value={draft.timezone}
            onChange={(event) => {
              change({ timezone: event.target.value });
            }}
          />
          <p className="wp-small wp-muted" data-testid="k8-sched-localtime">
            {t('k8.sched.localTime')}
          </p>
        </div>

        <div className="wp-field">
          <label className="wp-field__label" htmlFor="k8-sched-period">
            {t('k8.sched.period')}
          </label>
          <select
            className="wp-field__input"
            id="k8-sched-period"
            data-testid="k8-sched-period"
            value={draft.period}
            onChange={(event) => {
              change({ period: event.target.value as SchedulePeriod });
            }}
          >
            {SCHEDULE_PERIODS.map((period) => (
              <option key={period} value={period}>
                {t(`k8.sched.period.${period}` as MessageKey)}
              </option>
            ))}
          </select>
          <p className="wp-small wp-muted" data-testid="k8-sched-period-note">
            {t('k8.sched.periodNote')}
          </p>
        </div>

        <div className="wp-field">
          <label className="wp-field__label" htmlFor="k8-sched-recipients">
            {t('k8.sched.recipients')}
          </label>
          <input
            className="wp-field__input"
            id="k8-sched-recipients"
            data-testid="k8-sched-recipients"
            type="text"
            autoComplete="off"
            value={draft.recipients}
            onChange={(event) => {
              change({ recipients: event.target.value });
            }}
          />
          <p className="wp-small wp-muted" data-testid="k8-sched-recipients-hint">
            {t('k8.sched.recipientsHint')}
          </p>
        </div>

        <label className="wp-filters__radio" htmlFor="k8-sched-enabled">
          <input
            id="k8-sched-enabled"
            data-testid="k8-sched-enabled"
            type="checkbox"
            checked={draft.enabled}
            onChange={(event) => {
              change({ enabled: event.target.checked });
            }}
          />
          {t('k8.sched.enabled')}
        </label>

        {problems.length === 0 ? null : (
          <ul className="wp-list" data-testid="k8-sched-problems">
            {problems.map((problem) => (
              <li
                key={`${problem.field}.${problem.code}`}
                data-testid={`k8-sched-problem-${problem.field}`}
              >
                {t(`k8.sched.problem.${problem.field}.${problem.code}` as MessageKey)}
              </li>
            ))}
          </ul>
        )}

        {/* The honest disabled: it says "you have not finished describing the
            schedule", which is a statement about the operator's own draft. */}
        <button
          className="wp-button wp-button--primary"
          type="submit"
          data-testid="k8-sched-submit"
          disabled={problems.length > 0 || pending}
        >
          {pending ? t('k8.sched.submitting') : t('k8.sched.submit')}
        </button>
      </form>

      {created === null ? null : (
        <StatusNote severity="info" title={t('k8.sched.title')} testId="k8-sched-created">
          <p>{t('k8.sched.created', { name: created.name, nextRunAt: created.next_run_at })}</p>
          <p className="wp-small wp-muted" data-testid="k8-sched-created-id">
            {t('k8.sched.createdId', { id: created.id })}
          </p>
        </StatusNote>
      )}

      {create.status === 'error' && create.error !== null ? (
        <ApiErrorNote error={create.error} subject={t('k8.sched.title')} testId="k8-sched-error" />
      ) : null}
    </section>
  );
}
